<script setup lang="ts">
import { computed, ref, watch } from "vue";

import Button from "~/components/dumb/Button.vue";
import Checkbox from "~/components/dumb/Checkbox.vue";
import Input from "~/components/dumb/Input.vue";
import Modal from "~/components/dumb/Modal.vue";
import Toggle from "~/components/dumb/Toggle.vue";
import { ButtonSize, ButtonStyle, ModalWidth, WebhookEventKind } from "~/shared/enums";
import type { Webhook } from "~/shared/types";
import { useDataStore, useEnvironmentStore } from "~/stores";
import { getOrdersBetween } from "~/utils/orderManager";
import { makeValidateWebhookUrl } from "~/utils/validation";

const props = defineProps<{
  webhook: Webhook | undefined;
}>();

const emit = defineEmits<{
  close: [];
}>();

const dataStore = useDataStore();
const environmentStore = useEnvironmentStore();

const toggleRef = ref<InstanceType<typeof Toggle> | null>(null);

const titleInput = ref<InstanceType<typeof Input> | null>(null);
const urlInput = ref<InstanceType<typeof Input>>();

const initUrl = ref("");
const initTitle = ref("");
const enabled = ref(true);

const selectedEvents = ref(new Map<WebhookEventKind, boolean>());

const webhooks = computed<Webhook[]>(() => dataStore.webhookList);

const isEditable = computed(() => (!props.webhook ? false : props.webhook.duid !== undefined));
const modalTitle = computed(() => (isEditable.value ? "Edit endpoint" : "Add endpoint"));
const buttonText = computed(() => (isEditable.value ? "Save" : "Add endpoint"));

const eventKinds = computed(() => Object.values(WebhookEventKind));

const toggleEvent = (eventKind: WebhookEventKind) => {
  const currentValue = selectedEvents.value.get(eventKind) || false;
  selectedEvents.value.set(eventKind, !currentValue);
};

const getSelectedEventKinds = (): WebhookEventKind[] =>
  Array.from(selectedEvents.value.entries())
    .filter((e) => e[1])
    .map((e) => e[0]);

const updateUrl = (url: string) => {
  if (!urlInput?.value?.isValid || !props.webhook) {
    return;
  }
  dataStore.updateWebhook({
    duid: props.webhook.duid,
    url,
    title: titleInput?.value?.value ?? "",
    enabled: enabled.value,
    eventKinds: getSelectedEventKinds(),
  });
};

const toggleEnabled = (newValue: boolean) => {
  enabled.value = newValue;
};

const saveWebhook = () => {
  if (!urlInput?.value?.isValid) {
    return;
  }

  if (props.webhook?.duid) {
    dataStore.updateWebhook({
      duid: props.webhook.duid,
      url: urlInput.value.value,
      title: titleInput?.value?.value ?? "",
      enabled: enabled.value,
      eventKinds: getSelectedEventKinds(),
    });
  } else {
    const lastOrder = webhooks.value.length > 0 ? webhooks.value[webhooks.value.length - 1].order : undefined;
    const nextOrder = getOrdersBetween(lastOrder, undefined)?.[0] ?? "";

    dataStore.createWebhook(urlInput.value.value, nextOrder, {
      title: titleInput?.value?.value ?? "",
      enabled: enabled.value,
      eventKinds: getSelectedEventKinds(),
    });
  }
  emit("close");
};

const validateWebhookUrl = computed(() =>
  makeValidateWebhookUrl(environmentStore.urlBase, webhooks.value, props.webhook?.duid)
);

const urlIsInvalid = computed(() => !urlInput.value?.isValid || !validateWebhookUrl.value(urlInput.value.value));

watch(
  () => props.webhook,
  (webhook) => {
    if (webhook) {
      initUrl.value = webhook.url;
      initTitle.value = webhook.title;
      enabled.value = webhook.enabled;
      selectedEvents.value = new Map();
      Object.values(WebhookEventKind).forEach((eventKind) => {
        const isSelected = webhook.eventKinds?.includes(eventKind) ?? false;
        selectedEvents.value.set(eventKind, isSelected);
      });
    }
  },
  { immediate: true }
);
</script>

<template>
  <Modal
    :entity="webhook || {}"
    :title="modalTitle"
    hide-title
    :width="ModalWidth.M"
    custom-styles="!p-6 overflow-hidden h-full"
    @close="emit('close')">
    <div class="flex h-full flex-col gap-6">
      <span class="text-xl/6 font-medium text-md">{{ modalTitle }}</span>
      <Input ref="titleInput" :init-value="initTitle" label="Title (optional)" placeholder="Add a title" />
      <div class="flex flex-col gap-1">
        <Input
          ref="urlInput"
          :init-value="initUrl"
          input-type="url"
          required
          label="Webhook URL"
          placeholder="Add a URL"
          :validate="validateWebhookUrl"
          @finalize="updateUrl" />
        <Toggle ref="toggleRef" :value="enabled" label="Enabled" column @update="toggleEnabled" />
      </div>
      <div class="flex flex-col gap-1.5">
        <span class="text-base font-semibold text-md">Events</span>
        <span class="text-sm text-lt">Select events to listen to</span>
        <div
          class="mt-1.5 w-full divide-y overflow-hidden rounded-lg border bg-gray-50 border-md divide-md dark:bg-zinc-800/30">
          <div
            v-for="eventKind in eventKinds"
            :key="eventKind"
            class="group cursor-pointer"
            @click="toggleEvent(eventKind)"
            @keydown.enter="toggleEvent(eventKind)">
            <div class="flex flex-row items-center gap-3 px-4 py-2 group-hover:bg-md">
              <Checkbox :value="selectedEvents.get(eventKind) ?? false" :label="eventKind" />
              <span :class="selectedEvents.get(eventKind) ? 'text-md' : 'text-lt'" class="text-sm">
                {{ eventKind }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-end">
        <Button
          :btn-style="ButtonStyle.PRIMARY"
          :text="buttonText"
          :size="ButtonSize.LARGE"
          :disabled="urlIsInvalid"
          @click="saveWebhook" />
      </div>
    </div>
  </Modal>
</template>
